import React, {useMemo, useState} from 'react';
import styles from './ProjectStoriesTable.module.scss';
import {TableColumnConfig, Text, Spin, Button, DropdownMenu, Icon} from '@gravity-ui/uikit';
import {Pencil, TrashBin} from '@gravity-ui/icons';
import {DataTable} from '../../shared';
import {ProjectStoryModel} from '../../entities';
import {useInfiniteScroll} from '../../shared/hooks/useInfiniteScroll';
import {EditProjectStoryModal, DeleteProjectStoryModal} from '../../features';
import {useNavigate} from 'react-router';

interface ProjectStoriesTableProps {
  stories: ProjectStoryModel[];
  isLoading: boolean;
  fetchNextPage: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage: boolean;
}

export const ProjectStoriesTable = ({
  stories,
  isLoading,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
}: ProjectStoriesTableProps) => {
  const SKELETON_ROWS = 5;
  const navigate = useNavigate();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingStoryId, setEditingStoryId] = useState<string | null>(null);
  const [editingStory, setEditingStory] = useState<ProjectStoryModel | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingStoryId, setDeletingStoryId] = useState<string | null>(null);

  const handleRowClick = (story: ProjectStoryModel) => {
    navigate(`/content/project-stories/${story.id}`);
  };

  const handleEditStory = (story: ProjectStoryModel) => {
    setEditingStoryId(story.id);
    setEditingStory(story);
    setIsEditModalOpen(true);
  };

  const handleDeleteStory = (story: ProjectStoryModel) => {
    setDeletingStoryId(story.id);
    setIsDeleteModalOpen(true);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU');
  };

  const columns = useMemo<TableColumnConfig<ProjectStoryModel>[]>(
    () => [
      {
        id: 'title',
        name: 'Заголовок истории',
        template: (item: ProjectStoryModel) => <Text variant="body-1">{item.title}</Text>,
      },
      {
        id: 'date',
        name: 'Дата создания',
        template: (item: ProjectStoryModel) => <Text variant="body-1">{formatDate(item.date)}</Text>,
      },
      {
        id: 'priority',
        name: 'Приоритет',
        template: (item: ProjectStoryModel) => <Text variant="body-1">{item.priority}</Text>,
      },
      {
        id: 'more',
        name: () => <div className={styles.tableHeader}></div>,
        template: (item: ProjectStoryModel) => (
          <div className={styles.moreCell} onClick={(e) => e.stopPropagation()}>
            <DropdownMenu
              items={[
                {
                  iconStart: <Icon size={16} data={Pencil} />,
                  action: () => handleEditStory(item),
                  text: 'Редактировать',
                  className: styles.dropdownItem,
                },
                {
                  iconStart: <Icon size={16} data={TrashBin} />,
                  action: () => handleDeleteStory(item),
                  text: 'Удалить',
                  className: styles.dropdownItemDelete,
                },
              ]}
            />
          </div>
        ),
      },
    ],
    [],
  );

  const {loadMoreRef} = useInfiniteScroll({
    hasNextPage: !!hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  return (
    <div className={styles.tableContainer}>
      <DataTable<ProjectStoryModel>
        isLoading={isLoading && !isFetchingNextPage}
        data={stories || []}
        columns={columns}
        edgePadding={false}
        skeletonRowCount={SKELETON_ROWS}
        emptyStateTitle="Новости не найдены"
        emptyStateDescription="Новости с таким названием не найдены. Попробуйте изменить запрос"
        onRowClick={handleRowClick}
      />

      {isFetchingNextPage ? (
        <div className={styles.loadingSpinnerContainer}>
          <Spin size="m" />
        </div>
      ) : (
        hasNextPage && (
          <Button
            component={'div'}
            size="xl"
            width="max"
            view="normal"
            ref={loadMoreRef}
            className={styles.loadMoreTrigger}>
            <Text variant="body-2">Загрузить ещё</Text>
          </Button>
        )
      )}

      {editingStory && (
        <EditProjectStoryModal
          open={isEditModalOpen}
          setOpen={setIsEditModalOpen}
          storyId={editingStoryId}
          story={editingStory}
        />
      )}

      {deletingStoryId && (
        <DeleteProjectStoryModal open={isDeleteModalOpen} setOpen={setIsDeleteModalOpen} storyId={deletingStoryId} />
      )}
    </div>
  );
};
